import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/fr';
import Modal from '../../../../modals/Modal';
import Box from '../../../shared/atoms/Box';
import Text from '../../../shared/atoms/Text';
import Heading from '../../../shared/atoms/Heading';
import Button from '../../../shared/atoms/Button';
import CalendarColor56 from '../../../icons/CalendarColor56';
import * as api from '../../../../utils/api';

const AcceptCounterProposalModal = ({
  open,
  proposal,
  courseId,
  onClose,
  onAccepted,
  onError,
}) => {
  const { t, i18n } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const formatDateTime = (dateTimeStr) => {
    const m = moment(dateTimeStr);
    m.locale(i18n.language === 'fr' ? 'fr' : 'en');
    return m.format('dddd D MMMM YYYY [à] HH:mm');
  };

  const handleConfirm = async () => {
    if (!proposal || !courseId) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await api.post(
        `/courses/${courseId}/counter-proposals/${proposal.id}/accept`,
      );

      if (response.success) {
        onAccepted(response.data);
        onClose();
      } else if (response.error_code === 'SLOT_UNAVAILABLE') {
        setError(t('counterProposals.errorUnavailable'));
        if (onError) {
          onError('unavailable');
        }
      } else {
        setError(response.error_message || t('counterProposals.errorGeneric'));
        if (onError) {
          onError('generic');
        }
      }
    } catch (err) {
      setError(t('counterProposals.errorGeneric'));
      if (onError) {
        onError('generic');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  if (!proposal) return null;

  return (
    <Modal
      zIndex="1002"
      hasCloseAction={true}
      open={open}
      onRequestClose={handleClose}>
      <Box alignItems="center" mb={5}>
        <CalendarColor56 mb={3} />
        <Heading level={4} center mb={0}>
          {t('counterProposals.confirmTitle')}
        </Heading>
      </Box>

      <Box
        backgroundColor="gray.lightest"
        borderRadius="sm"
        p={5}
        mb={5}
        alignItems="center">
        <Text
          fontWeight={600}
          fontSize="18px"
          textAlign="center"
          style={{ textTransform: 'capitalize' }}>
          📅 {formatDateTime(proposal.proposed_slot)}
        </Text>
      </Box>

      <Text textAlign="center" color="gray.dark" mb={5} fontSize="16px">
        {t('counterProposals.confirmDescription')}
      </Text>

      {error && (
        <Box
          backgroundColor="red.lightest"
          borderColor="red.light"
          borderWidth="1px"
          borderStyle="solid"
          borderRadius="sm"
          p={3}
          mb={4}>
          <Text color="red.dark" textAlign="center">
            {error}
          </Text>
        </Box>
      )}

      <Box flexDirection="row" justifyContent="flex-end" gap={8}>
        <Button
          size="compact"
          variant="neutral"
          onClick={handleClose}
          disabled={isSubmitting}>
          {t('counterProposals.cancelButton')}
        </Button>
        <Button size="compact" onClick={handleConfirm} disabled={isSubmitting}>
          {isSubmitting ? '...' : t('counterProposals.confirmButton')}
        </Button>
      </Box>
    </Modal>
  );
};

export default AcceptCounterProposalModal;
